/**
 * Strategic Relevance configuration and assessed partnerships.
 *
 * PLACEHOLDER / ILLUSTRATIVE DATA. Classified INTERNAL.
 *
 * STORED INPUTS ONLY. Every score — dimension score, BaseScore, discount
 * factor, OpportunityScore — is derived at run time by `src/domain/sr`, so the
 * wizard, the dashboard, the portfolio table and the library always agree.
 * Partnership Net 2.0 carried three inconsistent scoring models plus a seed
 * whose arithmetic did not reconcile with any of them.
 */

import type {
  SrAuditEntry,
  SrConfiguration,
  SrContract,
  SrCriterionDef,
  SrDimensionDef,
  SrFactorDef,
  SrFactorKey,
  SrMatrixDef,
  SrPartner,
} from '../../contracts';

export const SR_DIMENSIONS: readonly SrDimensionDef[] = [
  { id: 'brand', name: 'Brand Impact', weight: 45, enabled: true, color: 'brand' },
  { id: 'business', name: 'Business Impact', weight: 45, enabled: true, color: 'commerce' },
  { id: 'people', name: 'People Impact', weight: 10, enabled: true, color: 'people' },
  { id: 'planet', name: 'Planet Impact', weight: 0, enabled: false, color: 'audience' },
  { id: 'alignment', name: 'Strategic Alignment', weight: 0, enabled: false, color: 'operations' },
];

type RawCriterion = readonly [name: string, weight: number, question: string];

const RAW_CRITERIA: Readonly<Record<string, readonly RawCriterion[]>> = {
  brand: [
    ['Brand Awareness', 25, 'How much does this partnership increase brand awareness?'],
    ['Brand Positioning', 25, 'How well does the partner align with brand positioning?'],
    ['Brand Uniqueness', 25, 'How unique is this partnership in the market?'],
    ['Brand Perception', 15, 'How does this impact brand perception?'],
    ['Brand Equity', 10, 'What is the impact on brand equity?'],
  ],
  business: [
    ['Revenue Impact', 30, 'What is the expected revenue impact?'],
    ['Market Access', 25, 'Does this provide access to new markets?'],
    ['Return on Investment', 20, 'What is the projected ROI?'],
    ['Growth Potential', 15, 'What is the long-term growth potential?'],
    ['Operational Efficiency', 10, 'Does this improve operational efficiency?'],
  ],
  people: [
    ['Employee Engagement', 30, 'How does this engage our people?'],
    ['Cultural Fit', 25, 'Does this align with our culture?'],
    ['Talent Attraction', 20, 'Does this help attract talent?'],
    ['Team Morale', 15, 'What is the impact on team morale?'],
    ['Skills Development', 10, 'Does this provide development opportunities?'],
  ],
  planet: [
    ['Sustainability Impact', 30, 'What is the environmental impact?'],
    ['Carbon Footprint', 25, 'How does this affect carbon footprint?'],
    ['Circular Economy', 20, 'Does this support circular economy principles?'],
    ['Biodiversity', 15, 'What is the impact on biodiversity?'],
    ['Resource Efficiency', 10, 'Does this improve resource efficiency?'],
  ],
  alignment: [
    ['Values Alignment', 30, 'How well do values align?'],
    ['Vision Compatibility', 25, 'Is there a shared vision?'],
    ['Stakeholder Alignment', 20, 'Do stakeholders align?'],
    ['Strategic Fit', 15, 'How strong is the strategic fit?'],
    ['Goal Congruence', 10, 'Are goals congruent?'],
  ],
};

export const SR_CRITERIA: Readonly<Record<string, readonly SrCriterionDef[]>> = Object.fromEntries(
  Object.entries(RAW_CRITERIA).map(([dim, list]) => [
    dim,
    list.map(([name, weight, question]) => ({ name, weight, question, enabled: true })),
  ]),
);

/**
 * Five factor levels each, as configured in Admin. The assessor's 0–100 slider
 * maps to a level; the level supplies the magnitude. Risk is subtracted; the
 * other two are added.
 */
export const SR_FACTORS: Readonly<Record<SrFactorKey, SrFactorDef>> = {
  risk: {
    label: 'Risk',
    levels: [
      ['Very High', 25],
      ['High', 15],
      ['Medium', 10],
      ['Low', 5],
      ['Very Low', 0],
    ],
    sign: -1,
  },
  potential: {
    label: 'Potential',
    levels: [
      ['Very High', 10],
      ['High', 7],
      ['Medium', 5],
      ['Low', 2],
      ['Very Low', 0],
    ],
    sign: 1,
  },
  alignment: {
    label: 'Strategic Alignment',
    levels: [
      ['Very High', 20],
      ['High', 15],
      ['Medium', 10],
      ['Low', 5],
      ['Very Low', 0],
    ],
    sign: 1,
  },
};

export const SR_MATRIX: SrMatrixDef = {
  xLabel: 'Business Score',
  yLabel: 'Brand Score',
  xThr: 50,
  yThr: 50,
  q: [
    'High engagement & brand partner',
    'Portfolio experience partner',
    'Develop or exit',
    'High business impact',
  ],
};

export const SR_PARTNERS: readonly SrPartner[] = [
  {
    id: 'visma',
    short: 'Visma',
    name: 'Team Visma | Lease A Bike',
    brand: 92,
    business: 85,
    people: 80,
    planet: 60,
    risk: 12,
    potential: 82,
    support: 85,
    investment: 6.8,
    svmd: 22.4,
    fit: 'Excellent',
    status: 'Locked',
  },
  {
    id: 'uae',
    short: 'UAE',
    name: 'UAE Team Emirates',
    brand: 88,
    business: 78,
    people: 72,
    planet: 50,
    risk: 18,
    potential: 58,
    support: 84,
    investment: 5.2,
    svmd: 18.9,
    fit: 'Strong',
    status: 'Submitted',
  },
  {
    id: 'ineos',
    short: 'Ineos',
    name: 'Ineos Grenadiers',
    brand: 75,
    business: 82,
    people: 65,
    planet: 45,
    risk: 48,
    potential: 76,
    support: 78,
    investment: 4.5,
    svmd: 15.2,
    fit: 'Good',
    status: 'Submitted',
  },
  {
    id: 'lidl',
    short: 'Lidl-Trek',
    name: 'Lidl-Trek',
    brand: 68,
    business: 90,
    people: 58,
    planet: 42,
    risk: 44,
    potential: 56,
    support: 58,
    investment: 3.9,
    svmd: 12.8,
    fit: 'Moderate',
    status: 'Draft',
  },
  {
    id: 'astana',
    short: 'Astana',
    name: 'Astana Qazaqstan',
    brand: 44,
    business: 41,
    people: 45,
    planet: 35,
    risk: 86,
    potential: 32,
    support: 34,
    investment: 2.1,
    svmd: 6.4,
    fit: 'Weak',
    status: 'Submitted',
  },
];

export const SR_CONTRACTS: readonly SrContract[] = [
  {
    id: 'contract-sr-1',
    name: 'Team Visma | Lease A Bike — WorldTour Title Partnership 2024–2027',
    partner: 'Team Visma | Lease A Bike',
    srId: 'visma',
    period: '2024 – 2027',
    investment: '€3.2M / year',
    status: 'active',
  },
  {
    id: 'contract-sr-2',
    name: 'UAE Team Emirates — Component & Drivetrain Supplier 2024–2026',
    partner: 'UAE Team Emirates',
    srId: 'uae',
    period: '2024 – 2026',
    investment: '€1.8M / year',
    status: 'active',
  },
  {
    id: 'contract-sr-3',
    name: 'Ineos Grenadiers — Technical Partnership 2023–2026',
    partner: 'Ineos Grenadiers',
    srId: 'ineos',
    period: '2023 – 2026',
    investment: '€2.1M / year',
    status: 'active',
  },
  {
    id: 'contract-sr-4',
    name: 'Lidl-Trek — Equipment Partnership 2025–2027',
    partner: 'Lidl-Trek',
    srId: 'lidl',
    period: '2025 – 2027',
    investment: '€1.4M / year',
    status: 'draft',
  },
  {
    id: 'contract-sr-5',
    name: 'Astana Qazaqstan — Drivetrain Sponsor 2024–2025',
    partner: 'Astana Qazaqstan',
    srId: 'astana',
    period: '2024 – 2025',
    investment: '€900K / year',
    status: 'active',
  },
];

export const SR_AUDIT: readonly SrAuditEntry[] = [
  {
    version: 'v1.0',
    date: '2026-01-15',
    by: 'Config Owner',
    changes: 'Initial configuration',
    used: '5 assessments',
  },
  {
    version: 'v0.9',
    date: '2026-01-10',
    by: 'Config Owner',
    changes: 'Updated Brand weights',
    used: '2 assessments',
  },
];

export const SR_CONFIGURATION: SrConfiguration = {
  name: 'Partnership Net Standard',
  version: '1.0',
  dims: SR_DIMENSIONS,
  criteria: SR_CRITERIA,
  factors: SR_FACTORS,
  matrix: SR_MATRIX,
  audit: SR_AUDIT,
};

/** The brand assessing these partnerships in the demo. */
export const SR_ASSESSING_BRAND = 'Shimano';
